import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { LinkButton } from '@pmi/dsm-react';
import { ChevronLeftSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import { AgreementApiModel } from '../../models/DealModel';
import { ProductApiModel } from '../../models/ProductModel';
import { WizardAction, WizardStep } from '.';
import AgreementProductSelector from './AgreementProductSelector';
import styles from './agreementCard.module.scss';
import { User } from '../../models/UserModels';
import AddUsers from './AddUser';

export interface CsvInvitees {
  valid: string[];
  invalid: string[];
  fileName?: string;
}
interface WizardContainerProps {
  deal: AgreementApiModel;
  // eslint-disable-next-line react/no-unused-prop-types
  defaultProducts?: ProductApiModel[];
  setActiveDeal?: Dispatch<SetStateAction<AgreementApiModel>>;
  initialStep?: WizardStep;
  fromDealSelector?: boolean;
  hasSingleAgreement?: boolean;
  people?: User[];
  action?: WizardAction;
  closeWizard: (e?: unknown) => void;
}

export default function WizardContainer({
  deal,
  setActiveDeal = undefined,
  initialStep = undefined,
  fromDealSelector = false,
  hasSingleAgreement = false,
  defaultProducts = [],
  people = undefined,
  action = 'add',
  closeWizard,
}: WizardContainerProps) {
  const wizardContainerRef = useRef<HTMLDivElement | null>(null);
  const [step, setStep] = useState<WizardStep>(initialStep || 'product');
  const [selectedProducts, setSelectedProducts] =
    useState<ProductApiModel[]>(defaultProducts);
  const [emailTags, setEmailTags] = useState<string[]>([]);
  const [addUsersMode, setAddUsersMode] = useState<
    'org' | 'email' | undefined
  >();

  const isStep1 =
    step === 'product' || step === 'group-products' || step === 'group-name';

  useEffect(() => {
    if (wizardContainerRef.current) {
      wizardContainerRef.current.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }

    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [step]);

  return (
    <div className="wizard-container" ref={wizardContainerRef}>
      {(!initialStep || initialStep === 'product') && (
        <div className="stepper-container my-3">
          <ul className="stepper mb-4">
            {fromDealSelector && !hasSingleAgreement && (
              <li
                className={`stepper-item ${
                  ['product', 'user-email'].includes(step) &&
                  'stepper-item_finished'
                }`}
              >
                <div className="stepper-point">
                  <span className="stepper-number">1</span>
                  <span className="stepper-title">Select Agreement</span>
                </div>
              </li>
            )}
            <li
              className={`stepper-item ${isStep1 && 'stepper-item_active'} ${
                ['user-email'].includes(step) && 'stepper-item_finished'
              }`}
            >
              <div className="stepper-point">
                <span className="stepper-number">
                  {fromDealSelector && !hasSingleAgreement ? '2' : '1'}
                </span>
                <span className="stepper-title">Select Products</span>
              </div>
            </li>
            {!people && (
              <li
                className={`stepper-item ${!isStep1 && 'stepper-item_active'}`}
              >
                <span className="stepper-point">
                  <span className="stepper-title">Invite People</span>
                  <span className="stepper-number">
                    {fromDealSelector && !hasSingleAgreement ? '3' : '2'}
                  </span>
                </span>
              </li>
            )}
          </ul>
        </div>
      )}
      <div className="wizard-container-inner">
        <div
          className={`d-sm-block d-block text-start ${styles.btnNavBackContainer}`}
        >
          {setActiveDeal && step === 'product' && (
            <LinkButton
              size="sm"
              iconAlign="left"
              titleText="Back to Agreements"
              icon={ChevronLeftSolidIcon}
              className="wizard-back-btn mb-4"
              onClick={() => setActiveDeal(undefined)}
            />
          )}
          {selectedProducts.length > 0 && step === 'user-email' && (
            <LinkButton
              size="sm"
              iconAlign="left"
              titleText={
                addUsersMode ? 'Back to invite options' : 'Back to Products'
              }
              icon={ChevronLeftSolidIcon}
              className="wizard-back-btn mb-4"
              onClick={
                addUsersMode
                  ? () => setAddUsersMode(undefined)
                  : () => setStep('product')
              }
            />
          )}
        </div>
        {step === 'product' && (
          <AgreementProductSelector
            setStep={setStep}
            agreement={deal}
            setProducts={setSelectedProducts}
            defaultProducts={selectedProducts}
            people={people}
            action={action}
            hasBackButton={!!setActiveDeal}
            onSuccess={closeWizard}
          />
        )}
        {step === 'user-email' && (
          <AddUsers
            products={selectedProducts}
            deal={deal}
            mode={addUsersMode}
            setMode={setAddUsersMode}
            emailTags={emailTags}
            setEmailTags={setEmailTags}
            onSuccess={closeWizard}
          />
        )}
      </div>
    </div>
  );
}
